import React, { useState, useEffect, useCallback } from "react";
import {
  IconSearch,
  IconPlus,
  IconChevronLeft,
  IconChevronRight,
  IconChevronDown,
  IconChevronUp,
  IconCalendar,
  IconUpload,
  IconDownload,
  IconCircleCheck,
  IconCircleX,
  IconClock,
  IconCalendarOff,
  IconPlaneDeparture,
  IconDeviceDesktop,
  IconQuestionMark,
  IconCalendarStar,
  IconUsers,
  IconUserCheck,
  IconUserX,
  IconClockPause,
  IconX,
  IconFilter,
} from "@tabler/icons-react";
import MarkAttendanceModal from "./components/MarkAttendanceModal";
import AttendanceDetailsModal from "./components/AttendanceDetailsModal";
import DeleteConfirmModal from "./components/DeleteConfirmModal";
import ImportAttendanceModal from "./components/ImportAttendanceModal";
import CompensatoryRequest from "./CompensatoryRequest";
import toast from "react-hot-toast";
import {
  fetchHrAttendance,
  fetchHrAttendanceCalendar,
  markHrAttendanceManually,
  deleteHrAttendance,
  fetchHrEmployees,
  fetchHrWorkingFrom,
  fetchHrDepartments,
} from "../../../apis/hrApi";

// ── Type Configs ────────────────────────────────────────────────────────────
const TYPE_CFG: Record<
  string,
  {
    label: string;
    sub?: string;
    bgColor: string;
    textColor: string;
    borderColor: string;
    icon: React.ReactNode;
  }
> = {
  EXTRA_TIME_WITHOUT_LATE: {
    label: "Present",
    sub: "EXTRA TIME WITHOUT LATE",
    bgColor: "bg-emerald-50",
    textColor: "text-emerald-600",
    borderColor: "border-emerald-200",
    icon: <IconCircleCheck size={16} stroke={2} />,
  },
  LATE: {
    label: "Late",
    sub: "LATE",
    bgColor: "bg-amber-50",
    textColor: "text-amber-600",
    borderColor: "border-amber-200",
    icon: <IconClock size={16} stroke={2} />,
  },
  AUTO_CLOCK_OUT: {
    label: "Auto Clock Out",
    sub: "AUTO CLOCK OUT",
    bgColor: "bg-slate-50",
    textColor: "text-slate-500",
    borderColor: "border-slate-200",
    icon: <IconDeviceDesktop size={16} stroke={2} />,
  },
  DIDNT_CLOCK_OUT: {
    label: "Didn't Clock Out",
    sub: "DIDNT CLOCK OUT",
    bgColor: "bg-orange-50",
    textColor: "text-orange-500",
    borderColor: "border-orange-200",
    icon: <IconQuestionMark size={16} stroke={2} />,
  },
  WEEK_OFF: {
    label: "Week Off",
    sub: "WEEK OFF",
    bgColor: "bg-cyan-50",
    textColor: "text-cyan-600",
    borderColor: "border-cyan-200",
    icon: <IconCalendarOff size={16} stroke={2} />,
  },
  ABSENT: {
    label: "Absent",
    bgColor: "bg-red-50",
    textColor: "text-red-500",
    borderColor: "border-red-200",
    icon: <IconCircleX size={16} stroke={2} />,
  },
  LEAVE: {
    label: "Leave",
    sub: "LEAVE",
    bgColor: "bg-purple-50",
    textColor: "text-purple-500",
    borderColor: "border-purple-200",
    icon: <IconPlaneDeparture size={16} stroke={2} />,
  },
  HOLIDAY: {
    label: "Holiday",
    sub: "HOLIDAY",
    bgColor: "bg-pink-50",
    textColor: "text-pink-500",
    borderColor: "border-pink-200",
    icon: <IconCalendarStar size={16} stroke={2} />,
  },
};

const getCfg = (type: string) =>
  TYPE_CFG[type] ?? {
    label: type,
    bgColor: "bg-gray-50",
    textColor: "text-gray-400",
    borderColor: "border-gray-200",
    icon: <IconQuestionMark size={16} stroke={2} />,
  };

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Helper to build a day record for the static mock dataset
const makeRecord = (day: number, type: string) => ({ day, type });

const EMPLOYEES = [
  {
    _id: "1",
    name: "Sushil Rathore",
    avatar: "",
    designation: "HR Executive",
    department: "Human Resources",
    records: [
      makeRecord(1, "EXTRA_TIME_WITHOUT_LATE"),
      makeRecord(2, "WEEK_OFF"),
      makeRecord(3, "WEEK_OFF"),
      makeRecord(4, "ABSENT"),
      makeRecord(5, "DIDNT_CLOCK_OUT"),
      makeRecord(6, "ABSENT"),
      makeRecord(7, "ABSENT"),
      makeRecord(8, "ABSENT"),
      makeRecord(9, "WEEK_OFF"),
      makeRecord(10, "ABSENT"),
      makeRecord(11, "ABSENT"),
      makeRecord(12, "ABSENT"),
      makeRecord(13, "ABSENT"),
      makeRecord(14, "ABSENT"),
      makeRecord(15, "ABSENT"),
      makeRecord(16, "WEEK_OFF"),
      makeRecord(17, "ABSENT"),
      makeRecord(18, "ABSENT"),
      makeRecord(19, "ABSENT"),
    ],
    total: 1,
    totalDays: 25,
  },
  {
    _id: "2",
    name: "Ruchi Rathore",
    avatar: "",
    designation: "Software Engineer",
    department: "IT & Support",
    records: [
      makeRecord(1, "EXTRA_TIME_WITHOUT_LATE"),
      makeRecord(2, "WEEK_OFF"),
      makeRecord(3, "AUTO_CLOCK_OUT"),
      makeRecord(4, "EXTRA_TIME_WITHOUT_LATE"),
      makeRecord(5, "ABSENT"),
      makeRecord(6, "ABSENT"),
      makeRecord(7, "LATE"),
      makeRecord(8, "AUTO_CLOCK_OUT"),
      makeRecord(9, "WEEK_OFF"),
      makeRecord(10, "EXTRA_TIME_WITHOUT_LATE"),
      makeRecord(11, "LATE"),
      makeRecord(12, "ABSENT"),
      makeRecord(13, "EXTRA_TIME_WITHOUT_LATE"),
      makeRecord(14, "ABSENT"),
      makeRecord(15, "EXTRA_TIME_WITHOUT_LATE"),
      makeRecord(16, "WEEK_OFF"),
      makeRecord(17, "ABSENT"),
      makeRecord(18, "EXTRA_TIME_WITHOUT_LATE"),
      makeRecord(19, "EXTRA_TIME_WITHOUT_LATE"),
    ],
    total: 9,
    totalDays: 25,
  },
  {
    _id: "3",
    name: "Neha",
    avatar: "",
    designation: "HR Executive",
    department: "Human Resources",
    records: [
      makeRecord(1, "ABSENT"),
      makeRecord(2, "WEEK_OFF"),
      makeRecord(3, "WEEK_OFF"),
      makeRecord(4, "ABSENT"),
      makeRecord(5, "ABSENT"),
      makeRecord(6, "ABSENT"),
      makeRecord(7, "ABSENT"),
      makeRecord(8, "LATE"),
      makeRecord(9, "AUTO_CLOCK_OUT"),
      makeRecord(10, "WEEK_OFF"),
      makeRecord(11, "ABSENT"),
      makeRecord(12, "ABSENT"),
      makeRecord(13, "ABSENT"),
      makeRecord(14, "ABSENT"),
      makeRecord(15, "ABSENT"),
      makeRecord(16, "WEEK_OFF"),
      makeRecord(17, "ABSENT"),
      makeRecord(18, "ABSENT"),
      makeRecord(19, "ABSENT"),
    ],
    total: 2,
    totalDays: 25,
  },
];

// ── Day Cell ─────────────────────────────────────────────────────────────────
function DayCell({
  type,
  onClick,
  date,
}: {
  type: string | null;
  onClick?: () => void;
  date?: string;
}) {
  const [hover, setHover] = useState(false);

  let isPast = false;
  if (date) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const cellDate = new Date(date);
    if (!isNaN(cellDate.getTime())) {
      cellDate.setHours(0, 0, 0, 0);
      isPast = cellDate < today;
    }
  }

  const effectiveType = type ? type : (isPast ? "ABSENT" : null);

  if (!effectiveType) {
    return (
      <td
        onClick={onClick}
        className="px-[3px] py-2.5 min-w-[42px] text-center border-b border-dashed border-gray-200/80 cursor-pointer hover:bg-gray-100/50 transition-colors"
      >
        <span className="text-gray-300 text-xs font-medium">—</span>
      </td>
    );
  }

  const cfg = getCfg(effectiveType);

  return (
    <td
      onClick={onClick}
      className="px-[3px] py-2.5 min-w-[42px] text-center border-b border-dashed border-gray-200/80 cursor-pointer"
    >
      <div
        className="relative group inline-flex"
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <span
          className={`flex items-center justify-center w-7 h-7 rounded-lg border 
            transition-all duration-200 ease-out
            hover:scale-110 hover:-translate-y-0.5 hover:shadow-md
            ${cfg.bgColor} ${cfg.borderColor} ${cfg.textColor}`}
        >
          {cfg.icon}
        </span>

        {/* Tooltip */}
        {hover && (
          <div className="pointer-events-none absolute -top-2 left-1/2 -translate-x-1/2 -translate-y-full z-30">
            <div className="relative">
              <div
                className="rounded-xl px-3 py-2 shadow-xl border border-gray-100 bg-white text-xs text-[#212121] whitespace-nowrap"
                style={{ backdropFilter: "blur(12px)" }}
              >
                <div className="flex items-center gap-2">
                  <span
                    className={`flex items-center justify-center w-6 h-6 rounded-lg border shrink-0 ${cfg.bgColor} ${cfg.borderColor} ${cfg.textColor}`}
                  >
                    {cfg.icon}
                  </span>
                  <div className="flex flex-col">
                    <span className="font-bold text-[#212121]">
                      {cfg.label}
                    </span>
                    {cfg.sub && (
                      <span className="text-[10px] text-gray-400 font-medium">
                        {cfg.sub}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="absolute left-1/2 top-full -translate-x-1/2 -mt-[3px] w-2 h-2 bg-white rotate-45 border-b border-r border-gray-100" />
            </div>
          </div>
        )}
      </div>
    </td>
  );
}

// ── Legend ───────────────────────────────────────────────────────────────────
function Legend() {
  const items = [
    {
      label: "Present",
      textColor: "text-emerald-600",
      bgColor: "bg-emerald-50",
      borderColor: "border-emerald-200",
      icon: <IconCircleCheck size={14} stroke={2} />,
    },
    {
      label: "Absent",
      textColor: "text-red-500",
      bgColor: "bg-red-50",
      borderColor: "border-red-200",
      icon: <IconCircleX size={14} stroke={2} />,
    },
    {
      label: "Late",
      textColor: "text-amber-600",
      bgColor: "bg-amber-50",
      borderColor: "border-amber-200",
      icon: <IconClock size={14} stroke={2} />,
    },
    {
      label: "Week Off",
      textColor: "text-cyan-600",
      bgColor: "bg-cyan-50",
      borderColor: "border-cyan-200",
      icon: <IconCalendarOff size={14} stroke={2} />,
    },
    {
      label: "Holiday",
      textColor: "text-pink-500",
      bgColor: "bg-pink-50",
      borderColor: "border-pink-200",
      icon: <IconCalendarStar size={14} stroke={2} />,
    },
    {
      label: "Leave",
      textColor: "text-purple-500",
      bgColor: "bg-purple-50",
      borderColor: "border-purple-200",
      icon: <IconPlaneDeparture size={14} stroke={2} />,
    },
    {
      label: "Auto Clock Out",
      textColor: "text-slate-500",
      bgColor: "bg-slate-50",
      borderColor: "border-slate-200",
      icon: <IconDeviceDesktop size={14} stroke={2} />,
    },
    {
      label: "Didn't Clock Out",
      textColor: "text-orange-500",
      bgColor: "bg-orange-50",
      borderColor: "border-orange-200",
      icon: <IconQuestionMark size={14} stroke={2} />,
    },
  ];

  return (
    <div className="bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-white/20 rounded-2xl px-5 py-3 shadow-sm">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
        <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mr-1">
          Legend
        </span>
        <div className="w-px h-4 bg-gray-200" />
        {items.map((it) => (
          <div key={it.label} className="flex items-center gap-1.5">
            <span
              className={`flex items-center justify-center w-5 h-5 rounded-md border ${it.bgColor} ${it.borderColor} ${it.textColor}`}
            >
              {it.icon}
            </span>
            <span
              className={`text-[11px] font-semibold ${it.textColor}`}
            >
              {it.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Summary Stats ────────────────────────────────────────────────────────────
function SummaryStats() {
  const stats = [
    {
      label: "Total Employees",
      value: "3",
      icon: <IconUsers size={20} stroke={1.5} />,
      bgColor: "bg-[#FBD037]/10",
      borderColor: "border-[#FBD037]/30",
      textColor: "text-[#212121]",
      iconColor: "text-[#C9A100]",
    },
    {
      label: "Present Today",
      value: "2",
      icon: <IconUserCheck size={20} stroke={1.5} />,
      bgColor: "bg-emerald-50",
      borderColor: "border-emerald-200",
      textColor: "text-emerald-700",
      iconColor: "text-emerald-500",
    },
    {
      label: "Absent Today",
      value: "1",
      icon: <IconUserX size={20} stroke={1.5} />,
      bgColor: "bg-red-50",
      borderColor: "border-red-200",
      textColor: "text-red-600",
      iconColor: "text-red-400",
    },
    {
      label: "Late Today",
      value: "0",
      icon: <IconClockPause size={20} stroke={1.5} />,
      bgColor: "bg-amber-50",
      borderColor: "border-amber-200",
      textColor: "text-amber-700",
      iconColor: "text-amber-500",
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {stats.map((s) => (
        <div
          key={s.label}
          className={`flex items-center gap-3 px-4 py-3 rounded-2xl border ${s.bgColor} ${s.borderColor} transition-all duration-200 hover:shadow-sm`}
        >
          <div
            className={`flex items-center justify-center w-9 h-9 rounded-xl ${s.bgColor} ${s.iconColor}`}
          >
            {s.icon}
          </div>
          <div>
            <div
              className={`text-lg font-bold leading-tight ${s.textColor}`}
            >
              {s.value}
            </div>
            <div className="text-[11px] text-gray-500 font-medium">
              {s.label}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────────
export default function AttendancePage() {
  const [search, setSearch] = useState("");
  const [showSearch, setShowSearch] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState<"attendance" | "compensatory">("attendance");

  // Import modal
  const [showImportModal, setShowImportModal] = useState(false);

  // Filter panel state
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [filterSelectedEmployee, setFilterSelectedEmployee] = useState("");
  const [hasActiveFilters, setHasActiveFilters] = useState(false);

  // Dynamic state hooks
  const [employeesList, setEmployeesList] = useState<any[]>([]);
  const [calendarEmployees, setCalendarEmployees] = useState<any[]>([]);
  const [attendanceLogs, setAttendanceLogs] = useState<any[]>([]);
  const [workingFromList, setWorkingFromList] = useState<any[]>([]);
  const [departmentsList, setDepartmentsList] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1); // 1-indexed (Jan = 1, etc.)
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedDepartmentId, setSelectedDepartmentId] = useState("");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [limit] = useState(10);

  // Sidebar Mode State
  const [sidebarMode, setSidebarMode] = useState<"add" | "edit" | "details" | "delete" | "filter" | "import" | null>(null);
  const [selectedEmp, setSelectedEmp] = useState<any>(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [selectedLog, setSelectedLog] = useState<any>(null);

  // Expanded employees state
  const [expandedEmployees, setExpandedEmployees] = useState<Set<string>>(new Set());

  const toggleExpanded = (empId: string) => {
    setExpandedEmployees((prev) => {
      const next = new Set(prev);
      if (next.has(empId)) {
        next.delete(empId);
      } else {
        next.add(empId);
      }
      return next;
    });
  };

  // Reset pagination on filter change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedMonth, selectedYear, selectedDepartmentId, search, filterSelectedEmployee]);

  // Dynamic month days
  const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();

  // Load backend data
  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      // 1. Fetch active employees (for dropdowns/modals)
      const empRes = await fetchHrEmployees(1, 1000);
      let employeesData = [];
      if (empRes.success && empRes.data?.employees) {
        employeesData = empRes.data.employees;
      }
      setEmployeesList(employeesData);

      // 2. Fetch departments
      const deptRes = await fetchHrDepartments(1, 100);
      if (deptRes.success && deptRes.data?.departments) {
        setDepartmentsList(deptRes.data.departments);
      }

      // 3. Fetch attendance logs using calendar endpoint
      const calRes = await fetchHrAttendanceCalendar(
        selectedMonth,
        selectedYear,
        currentPage,
        limit,
        filterSelectedEmployee || search || "",
        "",
        selectedDepartmentId || undefined
      );

      let calEmployeesData: any[] = [];
      let logsData: any[] = [];

      if (calRes.success && calRes.data) {
        const d = calRes.data;
        let rawList: any[] = [];
        
        if (Array.isArray(d)) {
          rawList = d;
        } else if (d && typeof d === "object") {
          const possible = d.employeeCalendars || d.list || d.calendar || d.attendances || d.employees || d.records || d.data;
          if (Array.isArray(possible)) {
            rawList = possible;
          } else {
            const found = Object.values(d).find((v) => Array.isArray(v));
            if (found) rawList = found as any[];
          }
        }

        if (rawList.length > 0) {
          const first = rawList[0];
          const isFlat = first && (first.attendanceDate || (first.date && !first.employee && !first.calendar));
          
          if (isFlat) {
            logsData = rawList;
            calEmployeesData = employeesData;
          } else {
            calEmployeesData = rawList.map((item: any) => {
              const empInfo = item.employee || item;
              const empId = empInfo.id || empInfo._id || item.employeeId;
              
              const nested = item.calendar || item.attendances || item.records || item.attendance || [];
              if (Array.isArray(nested)) {
                nested.forEach((log: any) => {
                  logsData.push({
                    ...log,
                    attendanceDate: log.date || log.attendanceDate,
                    employeeId: log.employeeId || empId
                  });
                });
              }
              
              const fullEmp = employeesData.find((e: any) => String(e.id || e._id) === String(empId)) || {};
              
              return {
                id: empId,
                _id: empId,
                firstName: empInfo.firstName || fullEmp.firstName || "",
                lastName: empInfo.lastName || fullEmp.lastName || "",
                profilePic: empInfo.profilePic || empInfo.avatar || fullEmp.profilePic || fullEmp.avatar || "",
                designationName: empInfo.designation || empInfo.designationName || empInfo.designation?.designationName || fullEmp.designationName || fullEmp.designation?.designationName || "",
                departmentName: empInfo.departmentName || empInfo.department?.departmentName || fullEmp.departmentName || fullEmp.department?.departmentName || "",
                ...fullEmp,
                ...empInfo
              };
            });
          }
        }
        
        if (d && typeof d === "object") {
          const pag = d.pagination || {};
          const total = pag.total || d.total || d.totalRecords || d.totalCount || rawList.length;
          setTotalRecords(total);
          setTotalPages(pag.totalPages || d.totalPages || Math.ceil(total / limit) || 1);
        } else {
          setTotalRecords(rawList.length);
          setTotalPages(Math.ceil(rawList.length / limit) || 1);
        }
      } else {
        // Fallback on failure
        setTotalRecords(employeesData.length);
        setTotalPages(Math.ceil(employeesData.length / limit) || 1);
      }
      
      setAttendanceLogs(logsData);
      setCalendarEmployees(calEmployeesData);
    } catch (err) {
      console.error("Error loading attendance data:", err);
      toast.error("Failed to load attendance logs");
      // Fallback
      setTotalRecords(employeesList.length);
      setTotalPages(Math.ceil(employeesList.length / limit) || 1);
    } finally {
      setIsLoading(false);
    }
  }, [selectedMonth, selectedYear, currentPage, limit, search, filterSelectedEmployee, selectedDepartmentId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load working locations
  const loadWorkingFrom = useCallback(async () => {
    try {
      const res = await fetchHrWorkingFrom(1, 100);
      if (res.success) {
        let list: any[] = [];
        if (Array.isArray(res.data)) {
          list = res.data;
        } else if (res.data && typeof res.data === "object") {
          const possible = res.data.workingFroms || res.data.list || res.data.data || res.data.workingFrom;
          if (Array.isArray(possible)) {
            list = possible;
          } else {
            const found = Object.values(res.data).find(Array.isArray);
            if (found) list = found as any[];
          }
        }
        console.log("[WorkingFrom] raw res.data:", JSON.stringify(res.data));
        console.log("[WorkingFrom] extracted list:", list);
        if (list.length > 0) console.log("[WorkingFrom] first item keys:", Object.keys(list[0]), list[0]);
        setWorkingFromList(list);
      }
    } catch (err) {
      console.error("Error loading working locations:", err);
    }
  }, []);

  useEffect(() => {
    loadWorkingFrom();
  }, [loadWorkingFrom]);

  const handleRefreshMeta = () => {
    loadData();
    loadWorkingFrom();
  };

  const handleSaveAttendance = async (payload: any) => {
    try {
      const employeeIds = Array.isArray(payload.employeeIds)
        ? payload.employeeIds
        : payload.employeeId
        ? [payload.employeeId]
        : [];

      if (employeeIds.length === 0) {
        toast.error("Please select at least one employee");
        return;
      }

      const type = payload.markBy?.toUpperCase() || "DATE";

      const formatTimestamp = (timeStr: string) => {
        if (!timeStr) return "";
        if (timeStr.includes("-") && timeStr.includes("+")) return timeStr;
        
        let hh = "09";
        let mm = "00";
        
        const clean = timeStr.trim();
        const parts = clean.split(":");
        if (parts.length >= 2) {
          hh = parts[0].padStart(2, "0");
          const minPart = parts[1].split(" ");
          mm = minPart[0].padStart(2, "0");
          if (minPart.length > 1) {
            const ampm = minPart[1].toUpperCase();
            let hoursInt = parseInt(hh, 10);
            if (ampm === "PM" && hoursInt < 12) hoursInt += 12;
            if (ampm === "AM" && hoursInt === 12) hoursInt = 0;
            hh = String(hoursInt).padStart(2, "0");
          }
        }
        
        const todayStr = new Date().toISOString().split("T")[0];
        return `${todayStr} ${hh}:${mm}:00.000+05:30`;
      };

      const clockInVal = payload.clockIn ? formatTimestamp(payload.clockIn) : "";
      const clockOutVal = payload.clockOut ? formatTimestamp(payload.clockOut) : "";

      const apiPayload: any = {
        employeeIds,
        markAttendanceType: type,
        workingFromId: payload.workingFrom || undefined,
        clockIn: clockInVal || undefined,
        clockOut: clockOutVal || undefined,
        isLate: payload.late === "Yes",
        isHalfDay: payload.halfDay === "Yes",
        overwriteAttendance: payload.overwrite || false,
        remarks: payload.remarks || ""
      };

      if (type === "DATE") {
        apiPayload.attendanceDate = payload.date || selectedDate;
      } else if (type === "MULTIPLE") {
        // Use directly-selected dates array if provided
        if (Array.isArray(payload.selectedDates) && payload.selectedDates.length > 0) {
          apiPayload.attendanceDates = payload.selectedDates;
        } else {
          // Fallback: generate from start/end range
          const dates: string[] = [];
          const start = payload.startDate;
          const end = payload.endDate;
          if (start && end) {
            const curr = new Date(start);
            const last = new Date(end);
            while (curr <= last) {
              dates.push(curr.toISOString().split("T")[0]);
              curr.setDate(curr.getDate() + 1);
            }
          }
          apiPayload.attendanceDates = dates;
        }
      } else if (type === "MONTH") {
        apiPayload.month = selectedMonth;
        apiPayload.year = selectedYear;
      }

      const res = await markHrAttendanceManually(apiPayload);
      if (res.success) {
        toast.success(sidebarMode === "add" ? "Attendance marked successfully!" : "Attendance log updated successfully!");
        setSidebarMode(null);
        loadData();
      } else {
        toast.error(res.message || "Failed to save attendance");
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to save attendance");
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedLog || !selectedLog.id) {
      toast.error("No selected attendance log to delete");
      return;
    }
    try {
      const res = await deleteHrAttendance(selectedLog.id);
      if (res.success) {
        toast.success("Attendance log deleted successfully!");
        setSidebarMode(null);
        loadData();
      } else {
        toast.error(res.message || "Failed to delete attendance log");
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to delete attendance log");
    }
  };

  // Build day headers
  const dayHeaders = Array.from({ length: daysInMonth }, (_, i) => {
    const d = new Date(selectedYear, selectedMonth - 1, i + 1);
    const isWeekend = d.getDay() === 0 || d.getDay() === 6;
    return { day: i + 1, dow: DAYS[d.getDay()], isWeekend };
  });

  // Helper to parse dates in various formats (space, slash, dash, ISO)
  const parseAttendanceDate = (dateStr: string): Date | null => {
    if (!dateStr) return null;
    const normalized = dateStr.replace(/[T\s/]+/g, "-");
    const parts = normalized.split("-");
    if (parts.length >= 3) {
      const y = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10);
      const d = parseInt(parts[2], 10);
      if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
        return new Date(y, m - 1, d);
      }
    }
    const parsed = new Date(dateStr);
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  // Dynamic mapping of backend employees to UI model
  const targetEmployees = calendarEmployees.length > 0 ? calendarEmployees : employeesList;
  const mappedEmployees = targetEmployees.map((emp: any) => {
    const fullName = `${emp.firstName || ""} ${emp.lastName || ""}`.trim() || "Unknown";
    const designation = emp.designationName || emp.designation?.designationName || "-";
    const department = emp.departmentName || emp.department?.departmentName || "-";
    
    const empLogs = Array.isArray(attendanceLogs)
      ? attendanceLogs.filter((log: any) => {
          if (!log) return false;
          const logEmpId = typeof log.employeeId === "object" && log.employeeId
            ? (log.employeeId.id || log.employeeId._id || "")
            : String(log.employeeId || "");
          const targetId = String(emp.id || emp._id || "");
          return logEmpId && targetId && logEmpId.toLowerCase() === targetId.toLowerCase();
        })
      : [];
    
    const records = empLogs.map((log: any) => {
      const dateObj = parseAttendanceDate(log.attendanceDate);
      if (!dateObj) return null;
      if (log.status === "FUTURE") return null;
      
      let uiStatus = "EXTRA_TIME_WITHOUT_LATE";
      if (log.status === "WEEK_OFF") {
        uiStatus = "WEEK_OFF";
      } else if (log.status === "LEAVE") {
        uiStatus = "LEAVE";
      } else if (log.status === "HOLIDAY") {
        uiStatus = "HOLIDAY";
      } else if (log.status === "PRESENT") {
        uiStatus = log.isLate ? "LATE" : "EXTRA_TIME_WITHOUT_LATE";
      } else if (log.status === "LATE") {
        uiStatus = "LATE";
      } else if (log.status === "ABSENT") {
        uiStatus = "ABSENT";
      } else {
        uiStatus = log.status || "EXTRA_TIME_WITHOUT_LATE";
      }
      
      return {
        id: log.id || log._id,
        date: dateObj,
        type: uiStatus,
        raw: log
      };
    }).filter((r): r is { id: string; date: Date; type: string; raw: any } => r !== null);
    
    const presentCount = records.filter(
      (r: any) => r.type === "EXTRA_TIME_WITHOUT_LATE" || r.type === "LATE" || r.type === "AUTO_CLOCK_OUT" || r.type === "PRESENT"
    ).length;
    
    return {
      _id: emp.id || emp._id,
      name: fullName,
      avatar: emp.profilePic || emp.avatar || "",
      designation,
      department,
      records,
      total: presentCount,
      totalDays: dayHeaders.length,
      raw: emp
    };
  });

  // Update active filters indicator
  useEffect(() => {
    setHasActiveFilters(!!(filterSelectedEmployee || selectedDepartmentId));
  }, [filterSelectedEmployee, selectedDepartmentId]);

  const clearFilters = () => {
    setFilterSelectedEmployee("");
    setSelectedDepartmentId("");
  };

  const filtered = calendarEmployees.length > 0
    ? mappedEmployees
    : mappedEmployees.filter(
        (e) =>
          (e.name.toLowerCase().includes(search.toLowerCase()) ||
           e.department.toLowerCase().includes(search.toLowerCase())) &&
          (!filterSelectedEmployee || e.name.toLowerCase().includes(filterSelectedEmployee.toLowerCase()))
      );

  // If Compensatory tab is active, render that component
  if (activeTab === "compensatory") {
    return (
      <div className="w-full mx-auto mt-2 space-y-4 animate-in fade-in duration-300">
        {/* Tab Switcher */}
        <div className="flex items-center gap-1 bg-white/60 backdrop-blur-xl border border-white/20 rounded-2xl px-2 py-1.5 shadow-sm w-fit">
          <button
            onClick={() => setActiveTab("attendance")}
            className="px-4 py-1.5 rounded-xl text-xs font-semibold transition-all duration-200 text-slate-500 hover:text-[#212121] hover:bg-white/60 cursor-pointer"
          >
            Attendance
          </button>
          <button
            onClick={() => setActiveTab("compensatory")}
            className="px-4 py-1.5 rounded-xl text-xs font-bold transition-all duration-200 bg-[#212121] text-white shadow-sm cursor-pointer"
          >
            Compensatory Request
          </button>
        </div>
        <CompensatoryRequest />
      </div>
    );
  }

  return (
    <>
    <div className="w-full mx-auto mt-2 space-y-4 animate-in fade-in duration-300">
      {/* Tab Switcher */}
      <div className="flex items-center gap-1 bg-white/60 backdrop-blur-xl border border-white/20 rounded-2xl px-2 py-1.5 shadow-sm w-fit">
        <button
          onClick={() => setActiveTab("attendance")}
          className="px-4 py-1.5 rounded-xl text-xs font-bold transition-all duration-200 bg-[#212121] text-white shadow-sm cursor-pointer"
        >
          Attendance
        </button>
        <button
          onClick={() => setActiveTab("compensatory")}
          className="px-4 py-1.5 rounded-xl text-xs font-semibold transition-all duration-200 text-slate-500 hover:text-[#212121] hover:bg-white/60 cursor-pointer"
        >
          Compensatory Request
        </button>
      </div>

      {/* ─── Summary Stats ───────────────────────────────────────────── */}
      <SummaryStats />

      {/* ─── Legend ──────────────────────────────────────────────────── */}
      <Legend />



      <div className="flex flex-col lg:flex-row gap-6 w-full items-stretch">
        {/* Left Column: Attendance Table Card */}
        <div className="flex-1 min-w-0 flex flex-col transition-all duration-300">
          {/* ─── Table Section (Header and Grid Joined) ──────────────────── */}
          <div className="bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl rounded-b-3xl rounded-tr-3xl border border-white/20 shadow-sm overflow-hidden">
        {/* Toolbar Controls */}
        <div className="px-6 py-1.5 flex flex-col lg:flex-row lg:items-center justify-end gap-4 border-b border-slate-50">
          <div className="flex items-center flex-wrap gap-2.5">
            {/* Filter Button */}
            <button
              onClick={() => setSidebarMode(sidebarMode === "filter" ? null : "filter")}
              className={`flex items-center gap-1.5 h-8 px-3 rounded-lg border text-xs font-semibold transition shadow-sm cursor-pointer ${
                sidebarMode === "filter" || hasActiveFilters
                  ? "border-[#FBD037] bg-[#FBD037]/10 text-[#212121]"
                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              }`}
              title="Filter"
            >
              <IconFilter size={14} stroke={2} />
              Filter
              {hasActiveFilters && (
                <span className="w-1.5 h-1.5 rounded-full bg-[#C9A100] ml-0.5" />
              )}
            </button>

            {/* Import / Export */}
            <div className="relative group">
              <button
                onClick={() => setSidebarMode(sidebarMode === "import" ? null : "import")}
                className={`border h-8 w-8 rounded-lg flex items-center justify-center transition shadow-sm hover:shadow-md hover:-translate-y-0.5 active:scale-95 cursor-pointer ${
                  sidebarMode === "import"
                    ? "border-[#FBD037] bg-[#FBD037]/10 text-[#212121]"
                    : "border-slate-200 bg-white hover:bg-slate-50 text-slate-700"
                }`}
                title="Import"
              >
                <IconUpload size={16} />
              </button>
              <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-[10px] font-bold px-2 py-1 rounded shadow-md whitespace-nowrap pointer-events-none z-50 animate-in fade-in duration-100">
                Import
              </div>
            </div>

            <div className="relative group">
              <button className="border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 h-8 w-8 rounded-lg flex items-center justify-center transition shadow-sm hover:shadow-md hover:-translate-y-0.5 active:scale-95 cursor-pointer" title="Export">
                <IconDownload size={16} />
              </button>
              <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-[10px] font-bold px-2 py-1 rounded shadow-md whitespace-nowrap pointer-events-none z-50 animate-in fade-in duration-100">
                Export
              </div>
            </div>

            {/* Sliding Search */}
            <div className="relative flex items-center group">
              <div
                onClick={() => !showSearch && setShowSearch(true)}
                className={`flex items-center transition-all duration-300 ease-in-out border border-slate-200 rounded-lg bg-white shadow-sm h-8 ${
                  showSearch ? "w-48 px-2 cursor-default" : "w-8 justify-center cursor-pointer hover:bg-slate-50 hover:shadow-md hover:-translate-y-0.5 active:scale-95"
                }`}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowSearch(!showSearch);
                    if (showSearch) {
                      setSearch("");
                    }
                  }}
                  className="flex items-center justify-center text-slate-700 focus:outline-none flex-shrink-0 cursor-pointer"
                >
                  <IconSearch size={16} />
                </button>
                {showSearch && (
                  <>
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search employee..."
                    autoFocus
                    className="flex-1 ml-2 bg-transparent text-xs text-slate-800 placeholder-slate-400 outline-none border-none focus:outline-none focus:ring-0"
                  />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSearch("");
                      setShowSearch(false);
                    }}
                    className="flex items-center justify-center p-0.5 ml-1 text-slate-400 hover:text-slate-600 focus:outline-none flex-shrink-0 cursor-pointer"
                  >
                    <IconX size={14} stroke={2.5} />
                  </button>
                  </>
                )}
              </div>
              {!showSearch && (
                <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-[10px] font-bold px-2 py-1 rounded shadow-md whitespace-nowrap pointer-events-none z-50 animate-in fade-in slide-in-from-bottom-1 duration-100">
                  Search
                </div>
              )}
            </div>

            {/* Add button inside toolbar */}
            <button
              onClick={() => {
                setSelectedEmp(null);
                setSelectedDate(`${selectedYear}-${String(selectedMonth).padStart(2, "0")}-01`);
                setSidebarMode("add");
              }}
              className="flex items-center justify-center w-8 h-8 rounded-lg bg-[#FBD037] hover:bg-[#FACC15] active:scale-95 transition shadow-sm hover:shadow-md cursor-pointer"
              title="Add Attendance"
            >
              <IconPlus stroke={2.5} size={16} className="text-[#212121]" />
            </button>
          </div>
        </div>

        {/* ─── Calendar Grid Table ─────────────────────────────────────── */}
        <div className="bg-transparent pt-0">
          <div className="overflow-auto bg-transparent max-h-[70vh] hide-scrollbar">
          <table className="w-full text-sm whitespace-nowrap">
            <thead className="text-[#131327]/70 border-gray-300 border-b border-dashed sticky top-0 z-20 shadow-sm bg-white/85">
              <tr>
                {/* Sticky employee col */}
                <th className="sticky left-0 z-20 bg-white/90 backdrop-blur-md px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-gray-500 min-w-[220px] border-r border-dashed border-gray-300">
                  Employee
                </th>
                {/* Day columns */}
                {dayHeaders.map(({ day, dow, isWeekend }) => (
                  <th
                    key={day}
                    className={`text-center px-[3px] py-2 min-w-[42px] ${
                      isWeekend ? "bg-[#FBD037]/5" : ""
                    }`}
                  >
                    <div className="text-[11px] font-bold text-[#212121]">
                      {day}
                    </div>
                    <div
                      className={`text-[9px] font-semibold uppercase tracking-wide ${
                        isWeekend ? "text-[#C9A100]" : "text-gray-400"
                      }`}
                    >
                      {dow}
                    </div>
                  </th>
                ))}
                {/* Sticky total col */}
                <th className="sticky right-0 z-20 bg-white/90 backdrop-blur-md px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-gray-500 text-center min-w-[72px] border-l border-dashed border-gray-300">
                  Total
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-transparent">
              {filtered.map((emp) => {
                // Build a map day→type
                const dayMap: Record<number, string> = {};
                emp.records.forEach((r) => {
                  dayMap[r.date.getDate()] = r.type;
                });

                // Calculate attendance percentage
                const pct =
                  emp.totalDays > 0
                    ? Math.round((emp.total / emp.totalDays) * 100)
                    : 0;
                const pctColor =
                  pct >= 75
                    ? "text-emerald-600"
                    : pct >= 50
                    ? "text-amber-600"
                    : "text-red-500";

                const isExpanded = expandedEmployees.has(emp._id);
                return (
                  <React.Fragment key={emp._id}>
                  <tr
                    className="hover:bg-[#FBD037]/10 group transition-colors duration-150 cursor-pointer"
                  >
                    {/* Employee cell */}
                    <td 
                      onClick={() => toggleExpanded(emp._id)}
                      className="sticky left-0 z-10 bg-white/90 backdrop-blur-md group-hover:bg-[#FBD037]/10 transition-colors p-3 min-w-[220px] border-r border-dashed border-gray-200/80 border-b border-b-gray-200/80 select-none cursor-pointer"
                    >
                      <div className="flex items-center gap-3">
                        <div className="text-gray-400 shrink-0">
                          {isExpanded ? (
                            <IconChevronUp size={16} stroke={2.5} />
                          ) : (
                            <IconChevronDown size={16} stroke={2.5} />
                          )}
                        </div>
                        <div className="w-10 h-10 rounded-xl bg-[#FBD037]/20 border border-[#FBD037]/40 flex items-center justify-center text-sm font-bold text-[#212121] flex-shrink-0">
                          {emp.name.charAt(0)}
                        </div>
                        <div>
                          <div className="text-[13px] font-semibold text-[#212121] whitespace-nowrap">
                            {emp.name}
                          </div>
                          <div className="text-[11px] text-gray-400 whitespace-nowrap">
                            {emp.designation} · {emp.department}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Day cells */}
                    {dayHeaders.map(({ day, isWeekend }) => {
                      const recordType = dayMap[day] ?? null;
                      const dateStr = `${selectedYear}-${String(selectedMonth).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                      const dayLog = emp.records.find((r: any) => r.date.getDate() === day);
                      
                      let isPast = false;
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const cellDate = new Date(dateStr);
                      if (!isNaN(cellDate.getTime())) {
                        cellDate.setHours(0, 0, 0, 0);
                        isPast = cellDate < today;
                      }
                      const effectiveType = recordType 
                        ? recordType 
                        : (isPast ? "ABSENT" : null);

                      const isPresentType =
                        effectiveType === "EXTRA_TIME_WITHOUT_LATE" ||
                        effectiveType === "LATE" ||
                        effectiveType === "PRESENT" ||
                        effectiveType === "AUTO_CLOCK_OUT" ||
                        effectiveType === "DIDNT_CLOCK_OUT";

                      return (
                        <DayCell
                          key={day}
                          type={effectiveType}
                          date={dateStr}
                          onClick={() => {
                            if (effectiveType) {
                              setSelectedEmp({
                                _id: emp._id,
                                name: emp.name,
                                avatar: emp.avatar,
                                designation: emp.designation,
                                department: emp.department
                              });
                              setSelectedDate(dateStr);
                              setSelectedLog(dayLog ? dayLog.raw : null);
                              if (isPresentType) {
                                setSidebarMode("details");
                              } else {
                                setSidebarMode("edit");
                              }
                            }
                          }}
                        />
                      );
                    })}

                    {/* Total cell */}
                    <td className="sticky right-0 z-10 bg-white/90 backdrop-blur-md group-hover:bg-[#FBD037]/10 transition-colors px-4 py-2 text-center border-l border-dashed border-gray-200/80 border-b border-b-gray-200/80">
                      <div className="flex flex-col items-center gap-0.5">
                        <span className="text-[13px] font-bold text-[#212121]">
                          {emp.total}
                          <span className="text-gray-400 font-normal">
                            /{emp.totalDays}
                          </span>
                        </span>
                        <span
                          className={`text-[10px] font-bold ${pctColor}`}
                        >
                          {pct}%
                        </span>
                      </div>
                    </td>
                  </tr>

                  {isExpanded && (
                    <tr className="bg-slate-50/50 text-xs text-gray-500 hover:bg-[#FBD037]/5 transition-colors duration-150">
                      {/* Sticky left cell for "Hours Worked." */}
                      <td className="sticky left-0 z-10 bg-slate-50/90 backdrop-blur-md p-2.5 pl-8 min-w-[220px] border-r border-dashed border-gray-200/80 border-b border-b-gray-200/80">
                        <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">
                          Hours Worked.
                        </div>
                      </td>
                      
                      {/* Daily hours cells */}
                      {dayHeaders.map(({ day, isWeekend }) => {
                        const recordType = dayMap[day] ?? null;
                        let displayHours = "-";
                        
                        if (recordType === "PRESENT" || recordType === "LATE" || recordType === "EXTRA_TIME_WITHOUT_LATE") {
                          const seed = (emp.name.charCodeAt(0) + day) % 5;
                          const hours = 8 + (seed % 2); // 8 or 9
                          const minutes = 15 + (seed * 9); // some minutes
                          displayHours = `${hours}h ${minutes}m`;
                        }
                        
                        return (
                          <td
                            key={day}
                            className={`text-center px-[3px] py-2 text-[10px] font-medium border-b border-b-gray-200/80 ${
                              isWeekend ? "bg-[#FBD037]/5 text-[#C9A100]" : "text-gray-500 font-semibold"
                            }`}
                          >
                            {displayHours}
                          </td>
                        );
                      })}
                      
                      {/* Sticky right cell for Total Hours */}
                      <td className="sticky right-0 z-10 bg-slate-50/90 backdrop-blur-md px-4 py-2 text-center border-l border-dashed border-gray-200/80 border-b border-b-gray-200/80 text-[10px] font-bold text-gray-600 whitespace-nowrap">
                        {emp.total > 0 ? `${emp.total * 8}h 30m - ${emp.totalDays * 8}h` : "0h - 0h"}
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          </div>
          
          {/* Pagination Controls */}
          {totalRecords > 0 && (
            <div className="flex justify-end px-6 py-3.5 text-xs text-gray-500 items-center gap-2.5 border-t border-slate-50">
              <span>
                Record {(currentPage - 1) * limit + 1}-{Math.min(currentPage * limit, totalRecords)} of{" "}
                {totalRecords}
              </span>
              <span className="mx-1">{limit} / page</span>
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-1 rounded-lg hover:bg-slate-50 disabled:opacity-20 transition cursor-pointer"
              >
                <IconChevronLeft size={16} />
              </button>
              <span className="font-semibold">
                {currentPage}/{totalPages}
              </span>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-1 rounded-lg hover:bg-slate-50 disabled:opacity-20 transition cursor-pointer"
              >
                <IconChevronRight size={16} />
              </button>
            </div>
          )}
        </div>
        </div>
      </div>

      {/* Right Column Sidebar Panel */}
      {sidebarMode !== null && (
        <div className="w-full lg:w-[480px] xl:w-[540px] shrink-0 flex flex-col transition-all duration-300">
          <div className="relative w-full bg-white h-full shadow-sm flex flex-col rounded-3xl border border-gray-100 overflow-hidden animate-in slide-in-from-right duration-300">
          {sidebarMode === "details" ? (
            <AttendanceDetailsModal
              isOpen={true}
              isSidebar={true}
              onClose={() => setSidebarMode(null)}
              employee={selectedEmp}
              date={selectedDate}
              onEdit={() => setSidebarMode("edit")}
              onDelete={() => setSidebarMode("delete")}
            />
          ) : sidebarMode === "add" || sidebarMode === "edit" ? (
            <MarkAttendanceModal
              isOpen={true}
              isSidebar={true}
              onClose={() => setSidebarMode(null)}
              mode={sidebarMode}
              selectedDate={selectedDate}
              employee={selectedEmp}
              onSave={handleSaveAttendance}
              employeesList={employeesList}
              workingFromList={workingFromList}
              departmentsList={departmentsList}
              onRefreshMeta={handleRefreshMeta}
            />
          ) : sidebarMode === "delete" ? (
            <DeleteConfirmModal
              isOpen={true}
              isSidebar={true}
              onClose={() => setSidebarMode("details")}
              onConfirm={handleDeleteConfirm}
              title="Delete Activity"
              message="Are you sure you want to delete this activity?"
            />
          ) : sidebarMode === "filter" ? (
            <div className="flex flex-col bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden h-full w-full p-6 space-y-6 animate-in fade-in duration-200">
              <div className="flex items-center justify-between pb-3.5 border-b border-gray-100 shrink-0">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-[#FBD037]/20 flex items-center justify-center">
                    <IconFilter size={16} className="text-[#C9A100]" />
                  </div>
                  <h2 className="text-base font-bold text-[#212121]">Filters</h2>
                </div>
                <button
                  type="button"
                  onClick={() => setSidebarMode(null)}
                  className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors text-slate-500 cursor-pointer"
                  title="Close"
                >
                  <IconX size={16} />
                </button>
              </div>

              <div className="space-y-4 flex-1 overflow-y-auto pr-1">
                {/* Department */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Department</label>
                  <select
                    value={selectedDepartmentId}
                    onChange={(e) => setSelectedDepartmentId(e.target.value)}
                    className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-[#FBD037]/40 focus:border-[#FBD037] text-xs text-[#212121] transition cursor-pointer"
                  >
                    <option value="">All Departments</option>
                    {departmentsList.map((d) => (
                      <option key={d.id || d._id} value={d.id || d._id}>
                        {d.departmentName || d.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Employee */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Employee</label>
                  <select
                    value={filterSelectedEmployee}
                    onChange={(e) => setFilterSelectedEmployee(e.target.value)}
                    className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-[#FBD037]/40 focus:border-[#FBD037] text-xs text-[#212121] transition cursor-pointer"
                  >
                    <option value="">Select Employee</option>
                    {employeesList.map((emp) => (
                      <option key={emp.id || emp._id} value={`${emp.firstName} ${emp.lastName}`.trim()}>
                        {`${emp.firstName || ""} ${emp.lastName || ""}`.trim()}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Month */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Month</label>
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(Number(e.target.value))}
                    className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-[#FBD037]/40 focus:border-[#FBD037] text-xs text-[#212121] transition cursor-pointer"
                  >
                    {Array.from({ length: 12 }, (_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {new Date(new Date().getFullYear(), i, 1).toLocaleString("default", { month: "long" })}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Year */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Year</label>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                    className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-[#FBD037]/40 focus:border-[#FBD037] text-xs text-[#212121] transition cursor-pointer"
                  >
                    {Array.from({ length: 6 }, (_, idx) => {
                      const yr = new Date().getFullYear() - 3 + idx;
                      return (
                        <option key={yr} value={yr}>{yr}</option>
                      );
                    })}
                  </select>
                </div>
              </div>

              {/* Actions */}
              <div className="pt-4 border-t border-gray-100 shrink-0 flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setSidebarMode(null);
                  }}
                  className="w-full bg-[#FBD037] hover:bg-[#FACC15] text-[#212121] font-bold text-xs py-2.5 rounded-xl transition cursor-pointer shadow-sm flex items-center justify-center gap-1.5"
                >
                  Apply Filters
                </button>
                {hasActiveFilters && (
                  <button
                    type="button"
                    onClick={() => {
                      clearFilters();
                      setSidebarMode(null);
                    }}
                    className="w-full bg-white hover:bg-slate-50 border border-gray-200 text-slate-600 font-semibold text-xs py-2.5 rounded-xl transition cursor-pointer"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            </div>
          ) : sidebarMode === "import" ? (
            <ImportAttendanceModal
              isOpen={true}
              isSidebar={true}
              onClose={() => setSidebarMode(null)}
              onImport={async (file) => {
                await new Promise((r) => setTimeout(r, 1200));
                toast.success("File imported successfully!");
                loadData();
              }}
            />
          ) : null}
          </div>
        </div>
      )}
      </div>
    </div>
    </>
  );
}

